import { useEffect, useState } from 'react';
import { Download, RefreshCw, UserPlus, CheckCircle, XCircle, DollarSign, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { formatBRL } from '../../lib/currency';
import { useToast } from '../../components/ui/ToastSystem';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

// ─── Types ─────────────────────────────────────────────────────────────────

interface AffiliateRow {
  id: string;
  user_id: string;
  affiliate_code: string;
  status: 'pending' | 'active' | 'suspended';
  commission_type: 'fixed' | 'percentage';
  commission_fixed_brl: number | null;
  commission_pct: number | null;
  pix_key: string | null;
  pix_key_type: string | null;
  tax_id: string | null;
  tax_type: string | null;
  legal_name: string | null;
  notes: string | null;
  created_at: string;
  // joined from profiles
  profile_name?: string | null;
  profile_email?: string | null;
  // computed
  referrals_count?: number;
  paid_referrals?: number;
  pending_commission_brl?: number;
  approved_commission_brl?: number;
  paid_commission_brl?: number;
}

interface CommissionRow {
  id: string;
  affiliate_id: string;
  referred_user_id: string | null;
  amount_brl: number;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  payment_batch_id: string | null;
  rejection_reason: string | null;
  notes: string | null;
  approved_at: string | null;
  paid_at: string | null;
  created_at: string;
  // joined
  affiliate_code?: string;
  affiliate_legal_name?: string | null;
  referred_email?: string | null;
}

interface BatchRow {
  id: string;
  period_label: string;
  total_affiliates: number;
  total_commissions: number;
  total_amount_brl: number;
  status: 'draft' | 'processing' | 'completed' | 'cancelled';
  notes: string | null;
  executed_at: string | null;
  created_at: string;
}

type Tab = 'affiliates' | 'commissions' | 'payments';

const STATUS_COLORS: Record<string, string> = {
  pending:   '#fbbf24',
  active:    '#4ade80',
  suspended: '#f87171',
  approved:  '#60a5fa',
  paid:      '#4ade80',
  rejected:  '#f87171',
  draft:     '#fbbf24',
  processing:'#60a5fa',
  completed: '#4ade80',
  cancelled: '#f87171',
};

// ─── Component ──────────────────────────────────────────────────────────────

export function AdminAffiliatesPage() {
  const { showToast } = useToast();
  const [tab, setTab] = useState<Tab>('affiliates');

  // ── Affiliates tab state
  const [affiliates,    setAffiliates]    = useState<AffiliateRow[]>([]);
  const [loadingAff,    setLoadingAff]    = useState(true);
  const [expandedAff,   setExpandedAff]   = useState<string | null>(null);
  const [editModal,     setEditModal]     = useState<AffiliateRow | null>(null);
  const [saving,        setSaving]        = useState(false);
  const [registerModal,   setRegisterModal]   = useState(false);
  const [registerQuery,   setRegisterQuery]   = useState('');
  const [registerResults, setRegisterResults] = useState<{ id: string; full_name: string | null; email: string | null; referral_code: string | null }[]>([]);
  const [selectedUser,    setSelectedUser]    = useState<{ id: string; full_name: string | null; email: string | null; referral_code: string | null } | null>(null);
  const [registerCode,    setRegisterCode]    = useState('');
  // Create-new-user sub-form inside register modal
  const [createSubForm,    setCreateSubForm]    = useState(false);
  const [newUserEmail,     setNewUserEmail]     = useState('');
  const [newUserName,      setNewUserName]      = useState('');
  const [newUserPwd,       setNewUserPwd]       = useState('');
  const [creatingUser,     setCreatingUser]     = useState(false);
  const [defaultCommBrl, setDefaultCommBrl] = useState(10);
  const [syncing,       setSyncing]       = useState(false);

  // ── Commissions tab state
  const [commissions,   setCommissions]   = useState<CommissionRow[]>([]);
  const [loadingComm,   setLoadingComm]   = useState(false);
  const [commFilter,    setCommFilter]    = useState<'all' | 'pending' | 'approved' | 'paid' | 'rejected'>('pending');
  const [bulkSelected,  setBulkSelected]  = useState<Set<string>>(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  // ── Payments tab state
  const [batches,       setBatches]       = useState<BatchRow[]>([]);
  const [loadingBatch,  setLoadingBatch]  = useState(false);
  const [batchModal,    setBatchModal]    = useState(false);
  const [batchLabel,    setBatchLabel]    = useState('');
  const [batchNotes,    setBatchNotes]    = useState('');
  const [batchMarkPaid, setBatchMarkPaid] = useState(false);
  const [creatingBatch, setCreatingBatch] = useState(false);
  const [batchPreview,  setBatchPreview]  = useState<{
    loading: boolean;
    totalGross: number;
    totalIr: number;
    cpfLines: { name: string; gross: number; ir: number }[];
    cnpjCount: number;
  } | null>(null);

  // ── Load on mount
  useEffect(() => {
    supabase.from('platform_settings').select('value').eq('key', 'affiliate_commission_brl').maybeSingle()
      .then(({ data }) => { if (data?.value) setDefaultCommBrl(parseFloat(String(data.value)) || 10); });
    loadAffiliates();
  }, []);

  useEffect(() => { if (tab === 'commissions') loadCommissions(); }, [tab, commFilter]);
  useEffect(() => { if (tab === 'payments')    loadBatches(); }, [tab]);

  // ── Data loaders

  async function loadAffiliates() {
    setLoadingAff(true);

    const { data: rows, error } = await supabase
      .from('affiliates')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) { console.error(error); setLoadingAff(false); return; }
    if (!rows?.length) { setAffiliates([]); setLoadingAff(false); return; }

    // Enrich with profile data
    const userIds = rows.map(r => r.user_id);
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email, referred_by')
      .in('id', userIds);

    const profileMap = new Map((profiles ?? []).map(p => [p.id, p]));

    // Commission summaries per affiliate
    const { data: commSummary } = await supabase
      .from('affiliate_commissions')
      .select('affiliate_id, amount_brl, status');

    const summaryByAff: Record<string, { pending: number; approved: number; paid: number }> = {};
    for (const c of commSummary ?? []) {
      if (!summaryByAff[c.affiliate_id]) summaryByAff[c.affiliate_id] = { pending: 0, approved: 0, paid: 0 };
      if (c.status === 'pending')  summaryByAff[c.affiliate_id].pending  += Number(c.amount_brl);
      if (c.status === 'approved') summaryByAff[c.affiliate_id].approved += Number(c.amount_brl);
      if (c.status === 'paid')     summaryByAff[c.affiliate_id].paid     += Number(c.amount_brl);
    }

    // Referral counts: profiles.referred_by = affiliate_code
    const codes = rows.map(r => r.affiliate_code).filter(Boolean);
    const { data: allRefs } = await supabase
      .from('profiles')
      .select('referred_by, subscription_tier')
      .in('referred_by', codes as string[]);

    const refCount: Record<string, number>  = {};
    const paidCount: Record<string, number> = {};
    for (const r of allRefs ?? []) {
      if (!r.referred_by) continue;
      refCount[r.referred_by] = (refCount[r.referred_by] ?? 0) + 1;
      if (['monthly', 'annual', 'astrologer_pro'].includes(r.subscription_tier)) {
        paidCount[r.referred_by] = (paidCount[r.referred_by] ?? 0) + 1;
      }
    }

    const enriched: AffiliateRow[] = rows.map(r => {
      const p = profileMap.get(r.user_id);
      const s = summaryByAff[r.id] ?? { pending: 0, approved: 0, paid: 0 };
      return {
        ...r,
        profile_name:            p?.full_name ?? null,
        profile_email:           p?.email ?? null,
        referrals_count:         refCount[r.affiliate_code]  ?? 0,
        paid_referrals:          paidCount[r.affiliate_code] ?? 0,
        pending_commission_brl:  s.pending,
        approved_commission_brl: s.approved,
        paid_commission_brl:     s.paid,
      };
    });

    setAffiliates(enriched);
    setLoadingAff(false);
  }

  async function loadCommissions() {
    setLoadingComm(true);
    let q = supabase
      .from('affiliate_commissions')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);

    if (commFilter !== 'all') q = q.eq('status', commFilter);

    const { data: rows, error } = await q;
    if (error) { console.error(error); setLoadingComm(false); return; }
    if (!rows?.length) { setCommissions([]); setLoadingComm(false); return; }

    // Enrich with affiliate + referred user info
    const affIds  = [...new Set(rows.map(r => r.affiliate_id))];
    const userIds = [...new Set(rows.map(r => r.referred_user_id).filter(Boolean))];

    const [{ data: affs }, { data: users }] = await Promise.all([
      supabase.from('affiliates').select('id, affiliate_code, legal_name').in('id', affIds),
      userIds.length
        ? supabase.from('profiles').select('id, email').in('id', userIds as string[])
        : Promise.resolve({ data: [] }),
    ]);

    const affMap  = new Map((affs  ?? []).map(a => [a.id,  a]));
    const userMap = new Map((users ?? []).map(u => [u.id, u]));

    const enriched: CommissionRow[] = rows.map(r => ({
      ...r,
      affiliate_code:        affMap.get(r.affiliate_id)?.affiliate_code ?? '',
      affiliate_legal_name:  affMap.get(r.affiliate_id)?.legal_name ?? null,
      referred_email:        r.referred_user_id ? userMap.get(r.referred_user_id)?.email ?? null : null,
    }));

    setCommissions(enriched);
    setLoadingComm(false);
  }

  async function loadBatches() {
    setLoadingBatch(true);
    const { data, error } = await supabase
      .from('affiliate_payment_batches')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) console.error(error);
    setBatches((data ?? []) as BatchRow[]);
    setLoadingBatch(false);
  }

  // ── Affiliate actions

  function generateCode(name: string | null): string {
    const prefix = (name ?? '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toUpperCase();
    const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
    return (prefix + suffix).slice(0, 8);
  }

  function closeRegisterModal() {
    setRegisterModal(false);
    setRegisterQuery('');
    setRegisterResults([]);
    setSelectedUser(null);
    setRegisterCode('');
    setCreateSubForm(false);
    setNewUserEmail(''); setNewUserName(''); setNewUserPwd('');
  }

  async function createUserAndSelect() {
    if (!newUserEmail.trim() || newUserPwd.length < 6) return;
    setCreatingUser(true);
    const { data, error } = await supabase.functions.invoke('admin-create-user', {
      body: { email: newUserEmail.trim(), password: newUserPwd, fullName: newUserName.trim() || undefined, subscriptionTier: 'free', role: 'user' },
    });
    setCreatingUser(false);
    if (error || data?.error) {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao criar usuário' });
      return;
    }
    const newUser = { id: data.data?.user?.id ?? data.data?.id, full_name: newUserName.trim() || null, email: newUserEmail.trim(), referral_code: null };
    setSelectedUser(newUser);
    setRegisterCode(generateCode(newUser.full_name));
    setCreateSubForm(false);
    setNewUserEmail(''); setNewUserName(''); setNewUserPwd('');
  }

  async function registerAffiliate() {
    if (!selectedUser || !registerCode.trim()) return;
    setSaving(true);
    const { data, error } = await supabase.functions.invoke('admin-update-affiliate', {
      body: {
        userId:            selectedUser.id,
        affiliateCode:     registerCode.trim().toUpperCase(),
        status:            'active',
        commissionType:    'fixed',
        commissionFixedBrl: defaultCommBrl,
      },
    });
    setSaving(false);
    if (error || data?.error) {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao registrar afiliado' });
    } else {
      showToast({ type: 'success', message: 'Afiliado registrado ✦' });
      closeRegisterModal();
      loadAffiliates();
    }
  }

  async function saveAffiliate() {
    if (!editModal) return;
    setSaving(true);
    const { data, error } = await supabase.functions.invoke('admin-update-affiliate', {
      body: {
        userId:              editModal.user_id,
        status:              editModal.status,
        commissionType:      editModal.commission_type,
        commissionFixedBrl:  editModal.commission_fixed_brl ?? undefined,
        commissionPct:       editModal.commission_pct ?? undefined,
        pixKey:              editModal.pix_key ?? undefined,
        pixKeyType:          editModal.pix_key_type ?? undefined,
        taxId:               editModal.tax_id ?? undefined,
        taxType:             editModal.tax_type ?? undefined,
        legalName:           editModal.legal_name ?? undefined,
        notes:               editModal.notes ?? undefined,
      },
    });
    setSaving(false);
    if (error || data?.error) {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao salvar' });
    } else {
      showToast({ type: 'success', message: 'Afiliado atualizado ✦' });
      setEditModal(null);
      loadAffiliates();
    }
  }

  async function syncCommissions() {
    setSyncing(true);
    const { data, error } = await supabase.functions.invoke('admin-sync-commissions', { body: {} });
    setSyncing(false);
    if (error || data?.error) {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao sincronizar' });
    } else {
      const n = data?.data?.created ?? 0;
      showToast({ type: 'success', message: n > 0 ? `${n} comissão(ões) criada(s)` : 'Tudo em dia!' });
      if (tab === 'commissions') loadCommissions();
    }
  }

  // ── Commission actions

  async function setCommissionStatus(ids: string[], status: 'approved' | 'rejected', reason?: string) {
    setBulkApproving(true);
    const patch: Record<string, unknown> = { status };
    if (status === 'approved') patch.approved_at = new Date().toISOString();
    if (status === 'rejected' && reason) patch.rejection_reason = reason;

    const { error } = await supabase
      .from('affiliate_commissions')
      .update(patch)
      .in('id', ids);

    setBulkApproving(false);
    if (error) {
      showToast({ type: 'error', message: 'Erro ao atualizar status' });
    } else {
      showToast({ type: 'success', message: status === 'approved' ? `${ids.length} aprovada(s)` : `${ids.length} rejeitada(s)` });
      setBulkSelected(new Set());
      loadCommissions();
    }
  }

  // ── Search users for registration (all users, no referral_code filter)
  async function searchUsers(q: string) {
    if (q.length < 2) { setRegisterResults([]); return; }
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, email, referral_code')
      .or(`email.ilike.%${q}%,full_name.ilike.%${q}%`)
      .limit(8);
    setRegisterResults(data ?? []);
  }

  // ── IR withholding (tabela progressiva 2026, CPF only)

  function calcIr(gross: number): number {
    if (gross <= 1903.98) return 0;
    if (gross <= 2826.65) return Math.round(gross * 0.075 * 100) / 100;
    if (gross <= 3751.05) return Math.round(gross * 0.15  * 100) / 100;
    if (gross <= 4664.68) return Math.round(gross * 0.225 * 100) / 100;
    return Math.round(gross * 0.275 * 100) / 100;
  }

  async function loadBatchPreview() {
    setBatchPreview({ loading: true, totalGross: 0, totalIr: 0, cpfLines: [], cnpjCount: 0 });

    const { data: comms } = await supabase
      .from('affiliate_commissions')
      .select('affiliate_id, amount_brl')
      .eq('status', 'approved')
      .is('payment_batch_id', null);

    if (!comms?.length) {
      setBatchPreview({ loading: false, totalGross: 0, totalIr: 0, cpfLines: [], cnpjCount: 0 });
      return;
    }

    const byAff: Record<string, number> = {};
    for (const c of comms) {
      byAff[c.affiliate_id] = (byAff[c.affiliate_id] ?? 0) + Number(c.amount_brl);
    }

    const { data: affs } = await supabase
      .from('affiliates')
      .select('id, legal_name, tax_type')
      .in('id', Object.keys(byAff));

    const affMap = new Map((affs ?? []).map(a => [a.id, a]));
    let totalGross = 0;
    let totalIr    = 0;
    const cpfLines: { name: string; gross: number; ir: number }[] = [];
    let cnpjCount  = 0;

    for (const [affId, gross] of Object.entries(byAff)) {
      totalGross += gross;
      const aff = affMap.get(affId);
      if (aff?.tax_type === 'cpf') {
        const ir = calcIr(gross);
        totalIr += ir;
        cpfLines.push({ name: aff.legal_name ?? affId, gross, ir });
      } else if (aff?.tax_type === 'cnpj') {
        cnpjCount++;
      }
    }

    setBatchPreview({ loading: false, totalGross, totalIr, cpfLines, cnpjCount });
  }

  // ── Payment batch

  async function createBatch() {
    if (!batchLabel.trim()) return;
    setCreatingBatch(true);
    const { data, error } = await supabase.functions.invoke('admin-create-payment-batch', {
      body: { periodLabel: batchLabel.trim(), notes: batchNotes || undefined, markPaid: batchMarkPaid },
    });
    setCreatingBatch(false);
    if (error || data?.error) {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao criar lote' });
    } else {
      showToast({ type: 'success', message: `Lote criado: ${data?.data?.commissionsLinked} comissões ✦` });
      setBatchModal(false);
      setBatchLabel(''); setBatchNotes(''); setBatchMarkPaid(false); setBatchPreview(null);
      loadBatches();
    }
  }

  // ── CSV export (affiliates)

  function exportCsv() {
    const headers = ['Nome', 'E-mail', 'Código', 'Status', 'Indicações', 'Pagantes', 'Pendente', 'Aprovado', 'Pago', 'PIX'];
    const rows = affiliates.map(a => [
      a.legal_name ?? a.profile_name ?? '', a.profile_email ?? '', a.affiliate_code, a.status,
      String(a.referrals_count ?? 0), String(a.paid_referrals ?? 0),
      String(a.pending_commission_brl ?? 0), String(a.approved_commission_brl ?? 0),
      String(a.paid_commission_brl ?? 0), a.pix_key ?? '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `afiliados_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  // ── Summary stats
  const totalAffiliates = affiliates.length;
  const activeAffiliates = affiliates.filter(a => a.status === 'active').length;
  const totalPending  = affiliates.reduce((s, a) => s + (a.pending_commission_brl  ?? 0), 0);
  const totalApproved = affiliates.reduce((s, a) => s + (a.approved_commission_brl ?? 0), 0);
  const totalPaid     = affiliates.reduce((s, a) => s + (a.paid_commission_brl     ?? 0), 0);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ flex: 1 }}>
          <AdminPageHeader
            title="Afiliados"
            objective="Programa de parceiros: gerencie afiliados, comissões e pagamentos. Registre novos parceiros, aprove comissões e processe lotes de pagamento mensal."
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '0.2rem' }}>
          <button className="btn btn-secondary btn-sm" onClick={syncCommissions} disabled={syncing}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <RefreshCw size={13} /> {syncing ? 'Sincronizando...' : 'Sincronizar Comissões'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={exportCsv}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <Download size={13} /> CSV
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setRegisterModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <UserPlus size={13} /> Registrar Afiliado
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', margin: '1.25rem 0' }}>
        {[
          { label: 'Total afiliados', value: totalAffiliates, sub: `${activeAffiliates} ativos`, color: 'var(--petrol)' },
          { label: 'Comissões pendentes', value: formatBRL(totalPending),  sub: 'aguardando aprovação', color: '#fbbf24' },
          { label: 'Comissões aprovadas', value: formatBRL(totalApproved), sub: 'prontas para pagamento', color: '#60a5fa' },
          { label: 'Total pago', value: formatBRL(totalPaid), sub: 'histórico acumulado', color: '#4ade80' },
        ].map(s => (
          <div key={s.label} className="card" style={{ flex: '1 1 160px' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0 0 0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</p>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.375rem', fontWeight: 700, color: s.color, margin: '0 0 0.15rem' }}>{s.value}</p>
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1.25rem', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '0.5rem' }}>
        {([
          ['affiliates', 'Afiliados'],
          ['commissions', 'Comissões'],
          ['payments', 'Pagamentos'],
        ] as const).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={tab === k ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}>
            {l}
          </button>
        ))}
      </div>

      {/* ── TAB: AFFILIATES ───────────────────────────────────────────── */}
      {tab === 'affiliates' && (
        loadingAff ? (
          <div className="card shimmer" style={{ height: '300px' }} />
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Afiliado</th>
                  <th>Código</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'center' }}>Indicações</th>
                  <th style={{ textAlign: 'center' }}>Pagantes</th>
                  <th style={{ textAlign: 'right' }}>Pendente</th>
                  <th style={{ textAlign: 'right' }}>Aprovado</th>
                  <th style={{ textAlign: 'right' }}>Pago</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {affiliates.map(a => (
                  <>
                    <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => setExpandedAff(expandedAff === a.id ? null : a.id)}>
                      <td>
                        <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                          {a.legal_name ?? a.profile_name ?? '-'}
                        </p>
                        <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>{a.profile_email}</p>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--petrol)' }}>{a.affiliate_code}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                          backgroundColor: `${STATUS_COLORS[a.status]}22`,
                          color: STATUS_COLORS[a.status], fontWeight: 600 }}>
                          {a.status}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--text-primary)' }}>{a.referrals_count ?? 0}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600, color: '#4ade80' }}>{a.paid_referrals ?? 0}</td>
                      <td style={{ textAlign: 'right', color: '#fbbf24', fontSize: 'var(--text-label)' }}>{formatBRL(a.pending_commission_brl ?? 0)}</td>
                      <td style={{ textAlign: 'right', color: '#60a5fa', fontSize: 'var(--text-label)' }}>{formatBRL(a.approved_commission_brl ?? 0)}</td>
                      <td style={{ textAlign: 'right', color: '#4ade80', fontSize: 'var(--text-label)' }}>{formatBRL(a.paid_commission_brl ?? 0)}</td>
                      <td style={{ color: 'var(--petrol)' }}>
                        {expandedAff === a.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </td>
                    </tr>
                    {expandedAff === a.id && (
                      <tr key={`${a.id}-detail`}>
                        <td colSpan={9} style={{ padding: '0.75rem 1rem', backgroundColor: 'var(--bg-elevated)' }}>
                          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: 'var(--text-caption)' }}>
                            <div>
                              <span style={{ color: 'var(--text-muted)' }}>Comissão: </span>
                              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                {a.commission_type === 'fixed'
                                  ? `R$ ${a.commission_fixed_brl ?? defaultCommBrl} / indicação paga`
                                  : `${((a.commission_pct ?? 0.20) * 100).toFixed(0)}% do valor`}
                              </span>
                            </div>
                            {a.pix_key && (
                              <div>
                                <span style={{ color: 'var(--text-muted)' }}>PIX ({a.pix_key_type}): </span>
                                <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace' }}>{a.pix_key}</span>
                              </div>
                            )}
                            {a.tax_id && (
                              <div>
                                <span style={{ color: 'var(--text-muted)' }}>{a.tax_type?.toUpperCase()}: </span>
                                <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace' }}>{a.tax_id}</span>
                              </div>
                            )}
                            {a.notes && (
                              <div>
                                <span style={{ color: 'var(--text-muted)' }}>Notas: </span>
                                <span style={{ color: 'var(--text-primary)' }}>{a.notes}</span>
                              </div>
                            )}
                            <div>
                              <span style={{ color: 'var(--text-muted)' }}>Cadastro: </span>
                              <span style={{ color: 'var(--text-primary)' }}>{new Date(a.created_at).toLocaleDateString('pt-BR')}</span>
                            </div>
                          </div>
                          <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            onClick={e => { e.stopPropagation(); setEditModal({ ...a }); }}>
                            <Settings size={13} /> Editar afiliado
                          </button>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
                {affiliates.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                    Nenhum afiliado registrado ainda
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── TAB: COMMISSIONS ─────────────────────────────────────────── */}
      {tab === 'commissions' && (
        <div>
          <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {(['all', 'pending', 'approved', 'paid', 'rejected'] as const).map(f => (
              <button key={f} onClick={() => { setCommFilter(f); setBulkSelected(new Set()); }}
                className={commFilter === f ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
                {f === 'all' ? 'Todas' : f === 'pending' ? 'Pendentes' : f === 'approved' ? 'Aprovadas' : f === 'paid' ? 'Pagas' : 'Rejeitadas'}
              </button>
            ))}
            {bulkSelected.size > 0 && (
              <div style={{ display: 'flex', gap: '0.35rem', marginLeft: 'auto' }}>
                <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', alignSelf: 'center' }}>
                  {bulkSelected.size} selecionada(s)
                </span>
                <button className="btn btn-primary btn-sm" disabled={bulkApproving}
                  onClick={() => setCommissionStatus([...bulkSelected], 'approved')}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <CheckCircle size={13} /> Aprovar
                </button>
                <button className="btn btn-secondary btn-sm" disabled={bulkApproving}
                  onClick={() => setCommissionStatus([...bulkSelected], 'rejected')}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: '#f87171' }}>
                  <XCircle size={13} /> Rejeitar
                </button>
              </div>
            )}
          </div>

          {loadingComm ? (
            <div className="card shimmer" style={{ height: '300px' }} />
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    {(commFilter === 'pending' || commFilter === 'approved') && (
                      <th style={{ width: '36px' }}>
                        <input type="checkbox"
                          checked={bulkSelected.size === commissions.length && commissions.length > 0}
                          onChange={e => setBulkSelected(e.target.checked ? new Set(commissions.map(c => c.id)) : new Set())} />
                      </th>
                    )}
                    <th>Afiliado</th>
                    <th>Usuário indicado</th>
                    <th style={{ textAlign: 'right' }}>Valor</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th>Data</th>
                    <th>Lote</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map(c => (
                    <tr key={c.id}>
                      {(commFilter === 'pending' || commFilter === 'approved') && (
                        <td>
                          <input type="checkbox"
                            checked={bulkSelected.has(c.id)}
                            onChange={e => {
                              const next = new Set(bulkSelected);
                              if (e.target.checked) next.add(c.id); else next.delete(c.id);
                              setBulkSelected(next);
                            }} />
                        </td>
                      )}
                      <td>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-label)', color: 'var(--text-primary)' }}>
                          {c.affiliate_legal_name ?? c.affiliate_code}
                        </p>
                        <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                          {c.affiliate_code}
                        </p>
                      </td>
                      <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                        {c.referred_email ?? '-'}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#4ade80', fontFamily: 'var(--font-display)' }}>
                        {formatBRL(c.amount_brl)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                          backgroundColor: `${STATUS_COLORS[c.status]}22`,
                          color: STATUS_COLORS[c.status], fontWeight: 600 }}>
                          {c.status}
                        </span>
                        {c.rejection_reason && (
                          <p style={{ margin: '0.2rem 0 0', fontSize: '10px', color: '#f87171' }}>{c.rejection_reason}</p>
                        )}
                      </td>
                      <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {new Date(c.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                        {c.payment_batch_id ? c.payment_batch_id.slice(0, 8) + '…' : '-'}
                      </td>
                    </tr>
                  ))}
                  {commissions.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                      Nenhuma comissão encontrada
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: PAYMENTS ────────────────────────────────────────────── */}
      {tab === 'payments' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button className="btn btn-primary btn-sm" onClick={() => { setBatchModal(true); loadBatchPreview(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <DollarSign size={13} /> Criar Lote de Pagamento
            </button>
          </div>
          {loadingBatch ? (
            <div className="card shimmer" style={{ height: '200px' }} />
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Período</th>
                    <th style={{ textAlign: 'center' }}>Afiliados</th>
                    <th style={{ textAlign: 'center' }}>Comissões</th>
                    <th style={{ textAlign: 'right' }}>Valor Total</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th>Criado</th>
                    <th>Executado</th>
                    <th>Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map(b => (
                    <tr key={b.id}>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{b.period_label}</td>
                      <td style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>{b.total_affiliates}</td>
                      <td style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>{b.total_commissions}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#4ade80', fontFamily: 'var(--font-display)' }}>
                        {formatBRL(b.total_amount_brl)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                          backgroundColor: `${STATUS_COLORS[b.status]}22`,
                          color: STATUS_COLORS[b.status], fontWeight: 600 }}>
                          {b.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {new Date(b.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {b.executed_at ? new Date(b.executed_at).toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {b.notes ?? '-'}
                      </td>
                    </tr>
                  ))}
                  {batches.length === 0 && (
                    <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                      Nenhum lote de pagamento criado ainda
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── MODAL: Register affiliate ─────────────────────────────────── */}
      {registerModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '440px', width: '100%' }}>
            <h2 style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <UserPlus size={16} /> Registrar Afiliado
            </h2>

            {/* Step 1: search or create new user */}
            {!selectedUser ? (
              <>
                {!createSubForm ? (
                  <>
                    <div className="form-group">
                      <label className="form-label">Buscar usuário existente (e-mail ou nome)</label>
                      <input type="text" className="form-input" value={registerQuery}
                        onChange={e => { setRegisterQuery(e.target.value); searchUsers(e.target.value); }}
                        placeholder="Digite para buscar..." autoFocus />
                    </div>
                    {registerResults.length > 0 && (
                      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginBottom: '0.75rem' }}>
                        {registerResults.map(u => (
                          <button key={u.id}
                            onClick={() => {
                              setSelectedUser(u);
                              setRegisterResults([]);
                              setRegisterQuery('');
                              setRegisterCode(u.referral_code ?? generateCode(u.full_name));
                            }}
                            style={{ width: '100%', padding: '0.6rem 0.875rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderBottom: '1px solid var(--border-subtle)' }}>
                            <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>{u.full_name ?? '-'}</p>
                            <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
                              {u.email}
                              {u.referral_code && <> · <span style={{ fontFamily: 'monospace', color: 'var(--petrol)' }}>{u.referral_code}</span></>}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                    {registerQuery.length >= 2 && registerResults.length === 0 && (
                      <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                        Nenhum usuário encontrado.
                      </p>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>Afiliado sem cadastro?</span>
                      <button className="btn btn-secondary btn-sm" onClick={() => setCreateSubForm(true)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <UserPlus size={13} /> Criar novo usuário
                      </button>
                    </div>
                    <button className="btn btn-ghost btn-sm" style={{ marginTop: '0.75rem' }} onClick={closeRegisterModal}>Fechar</button>
                  </>
                ) : (
                  /* Create new user sub-form */
                  <>
                    <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, marginBottom: '0.75rem' }}>Criar novo usuário</p>
                    <div className="form-group">
                      <label className="form-label">E-mail *</label>
                      <input type="email" className="form-input" value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} placeholder="afiliado@email.com" autoFocus />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Nome completo</label>
                      <input type="text" className="form-input" value={newUserName} onChange={e => setNewUserName(e.target.value)} placeholder="Nome do afiliado" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Senha provisória * (mín. 6 caracteres)</label>
                      <input type="password" className="form-input" value={newUserPwd} onChange={e => setNewUserPwd(e.target.value)} placeholder="••••••••" />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn btn-primary btn-sm" onClick={createUserAndSelect}
                        disabled={creatingUser || !newUserEmail.trim() || newUserPwd.length < 6}>
                        {creatingUser ? 'Criando...' : 'Criar e selecionar'}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setCreateSubForm(false); setNewUserEmail(''); setNewUserName(''); setNewUserPwd(''); }}>
                        Voltar
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              /* Step 2: configure affiliate code + commission */
              <>
                <div style={{ padding: '0.6rem 0.875rem', backgroundColor: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>{selectedUser.full_name ?? '-'}</p>
                    <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>{selectedUser.email}</p>
                  </div>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: 'var(--petrol)', textDecoration: 'underline', padding: 0 }}
                    onClick={() => { setSelectedUser(null); setRegisterCode(''); }}>
                    Trocar
                  </button>
                </div>

                <div className="form-group">
                  <label className="form-label">Código de afiliado *</label>
                  <input type="text" className="form-input" value={registerCode}
                    onChange={e => setRegisterCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                    placeholder="Ex: MARIA2026" maxLength={20} autoFocus />
                  <p className="form-hint">
                    Somente letras e números, sem espaços. Será o link de indicação: /ref/<strong>{registerCode || '...'}</strong>
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">Comissão inicial por indicação paga (R$)</label>
                  <input type="number" className="form-input" min={0} step={1} value={defaultCommBrl}
                    onChange={e => setDefaultCommBrl(parseFloat(e.target.value) || 0)} />
                  <p className="form-hint">Editável depois.</p>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-primary btn-sm" onClick={registerAffiliate}
                    disabled={saving || !registerCode.trim()}>
                    {saving ? 'Registrando...' : 'Registrar afiliado'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={closeRegisterModal}>Cancelar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL: Edit affiliate ─────────────────────────────────────── */}
      {editModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '480px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Settings size={16} /> Editar Afiliado — {editModal.legal_name ?? editModal.profile_name ?? editModal.affiliate_code}
            </h2>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={editModal.status}
                onChange={e => setEditModal(p => p ? { ...p, status: e.target.value as AffiliateRow['status'] } : p)}>
                <option value="pending">Pendente</option>
                <option value="active">Ativo</option>
                <option value="suspended">Suspenso</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Tipo de comissão</label>
              <select className="form-select" value={editModal.commission_type}
                onChange={e => setEditModal(p => p ? { ...p, commission_type: e.target.value as 'fixed' | 'percentage' } : p)}>
                <option value="fixed">Valor fixo (R$)</option>
                <option value="percentage">Percentual (%)</option>
              </select>
            </div>

            {editModal.commission_type === 'fixed' ? (
              <div className="form-group">
                <label className="form-label">Valor fixo por indicação paga (R$)</label>
                <input type="number" className="form-input" min={0} step={1}
                  value={editModal.commission_fixed_brl ?? defaultCommBrl}
                  onChange={e => setEditModal(p => p ? { ...p, commission_fixed_brl: parseFloat(e.target.value) || 0 } : p)} />
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Percentual (ex: 0.20 = 20%)</label>
                <input type="number" className="form-input" min={0} max={1} step={0.01}
                  value={editModal.commission_pct ?? 0.20}
                  onChange={e => setEditModal(p => p ? { ...p, commission_pct: parseFloat(e.target.value) || 0 } : p)} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Nome legal / razão social</label>
              <input type="text" className="form-input" value={editModal.legal_name ?? ''}
                onChange={e => setEditModal(p => p ? { ...p, legal_name: e.target.value || null } : p)} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group">
                <label className="form-label">Tipo de documento</label>
                <select className="form-select" value={editModal.tax_type ?? ''}
                  onChange={e => setEditModal(p => p ? { ...p, tax_type: (e.target.value || null) as AffiliateRow['tax_type'] } : p)}>
                  <option value="">Não informado</option>
                  <option value="cpf">CPF</option>
                  <option value="cnpj">CNPJ</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">CPF / CNPJ</label>
                <input type="text" className="form-input" value={editModal.tax_id ?? ''}
                  placeholder="000.000.000-00"
                  onChange={e => setEditModal(p => p ? { ...p, tax_id: e.target.value || null } : p)} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group">
                <label className="form-label">Tipo de chave PIX</label>
                <select className="form-select" value={editModal.pix_key_type ?? ''}
                  onChange={e => setEditModal(p => p ? { ...p, pix_key_type: (e.target.value || null) as AffiliateRow['pix_key_type'] } : p)}>
                  <option value="">Não informado</option>
                  <option value="cpf">CPF</option>
                  <option value="cnpj">CNPJ</option>
                  <option value="email">E-mail</option>
                  <option value="phone">Telefone</option>
                  <option value="random">Aleatória</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Chave PIX</label>
                <input type="text" className="form-input" value={editModal.pix_key ?? ''}
                  onChange={e => setEditModal(p => p ? { ...p, pix_key: e.target.value || null } : p)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notas internas</label>
              <textarea className="form-input" rows={2} value={editModal.notes ?? ''}
                style={{ resize: 'vertical' }}
                onChange={e => setEditModal(p => p ? { ...p, notes: e.target.value || null } : p)} />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={saveAffiliate} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Create payment batch ───────────────────────────────── */}
      {batchModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '420px', width: '100%' }}>
            <h2 style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <DollarSign size={16} /> Criar Lote de Pagamento
            </h2>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '1rem' }}>
              Agrupa todas as comissões aprovadas (sem lote) em um novo lote. Confirme após gerar o PIX externamente.
            </p>
            <div className="form-group">
              <label className="form-label">Rótulo do período *</label>
              <input type="text" className="form-input" value={batchLabel}
                placeholder="Ex: Junho 2026" onChange={e => setBatchLabel(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Notas (opcional)</label>
              <textarea className="form-input" rows={2} value={batchNotes}
                style={{ resize: 'vertical' }} onChange={e => setBatchNotes(e.target.value)} />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="batchPaid" checked={batchMarkPaid}
                onChange={e => setBatchMarkPaid(e.target.checked)} />
              <label htmlFor="batchPaid" className="form-label" style={{ margin: 0 }}>
                Marcar como pago imediatamente
              </label>
            </div>
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '1rem' }}>
              Se desmarcado, o lote fica como "rascunho" para você confirmar depois.
            </p>

            {/* IR withholding preview */}
            {batchPreview && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)' }}>
                {batchPreview.loading ? (
                  <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontStyle: 'italic' }}>Calculando IR...</p>
                ) : batchPreview.totalGross === 0 ? (
                  <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: '#fbbf24' }}>Nenhuma comissão aprovada sem lote.</p>
                ) : (
                  <>
                    <p style={{ margin: '0 0 0.5rem', fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--text-primary)' }}>
                      Prévia do lote
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-muted)' }}>Total bruto:</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formatBRL(batchPreview.totalGross)}</span>
                      </div>
                      {batchPreview.cpfLines.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--text-muted)' }}>IR retido estimado (CPF):</span>
                          <span style={{ color: '#f87171', fontWeight: 600 }}>-{formatBRL(batchPreview.totalIr)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border-subtle)', paddingTop: '0.25rem', marginTop: '0.15rem' }}>
                        <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Líquido estimado:</span>
                        <span style={{ color: '#4ade80', fontWeight: 700 }}>{formatBRL(batchPreview.totalGross - batchPreview.totalIr)}</span>
                      </div>
                    </div>
                    {batchPreview.cpfLines.length > 0 && (
                      <details style={{ marginTop: '0.5rem' }}>
                        <summary style={{ fontSize: '11px', color: 'var(--text-muted)', cursor: 'pointer' }}>
                          IR por afiliado CPF ({batchPreview.cpfLines.length})
                        </summary>
                        <div style={{ marginTop: '0.35rem', display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                          {batchPreview.cpfLines.map((l, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                              <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>{l.name}</span>
                              <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                {formatBRL(l.gross)} → IR: <span style={{ color: '#f87171' }}>{l.ir > 0 ? formatBRL(l.ir) : 'isento'}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                    {batchPreview.cnpjCount > 0 && (
                      <p style={{ margin: '0.4rem 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
                        {batchPreview.cnpjCount} afiliado(s) CNPJ: IR via nota fiscal (não retido aqui).
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={createBatch}
                disabled={creatingBatch || !batchLabel.trim()}>
                {creatingBatch ? 'Criando...' : 'Criar lote'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setBatchModal(false); setBatchPreview(null); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
